/**
 * Chat Store — Zustand + Immer store for AI chat panel.
 * Manages messages, streaming state, and panel visibility.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { authHeaders, useAuthStore } from './auth';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  isOpen: boolean;
  isLoadingHistory: boolean;
  currentModelId: string | null;
  error: string | null;
}

export interface ChatActions {
  sendMessage: (content: string, modelId: string) => Promise<void>;
  loadHistory: (modelId: string) => Promise<void>;
  clearChat: (modelId?: string) => void;
  togglePanel: () => void;
  setOpen: (open: boolean) => void;
}

// Active AbortController for cancelling in-flight requests
let abortController: AbortController | null = null;

export const useChatStore = create<ChatState & ChatActions>()(
  immer((set, get) => ({
    messages: [],
    isStreaming: false,
    isOpen: false,
    isLoadingHistory: false,
    currentModelId: null,
    error: null,

    loadHistory: async (modelId: string) => {
      // Skip if already loaded for this model
      if (get().currentModelId === modelId) return;

      set((state) => {
        state.isLoadingHistory = true;
        state.messages = [];
        state.currentModelId = modelId;
        state.error = null;
      });

      try {
        const resp = await fetch(`/api/v1/chat/history/${modelId}`, {
          headers: authHeaders(),
        });

        if (resp.status === 401) {
          useAuthStore.getState().logout();
          return;
        }

        if (!resp.ok) {
          set((state) => { state.isLoadingHistory = false; });
          return;
        }

        const records = await resp.json();
        const messages: ChatMessage[] = records.map((r: { role: string; content: string }) => ({
          role: r.role as 'user' | 'assistant',
          content: r.content,
        }));

        set((state) => {
          state.messages = messages;
          state.isLoadingHistory = false;
        });
      } catch {
        set((state) => { state.isLoadingHistory = false; });
      }
    },

    sendMessage: async (content: string, modelId: string) => {
      if (get().isStreaming) return;

      // Add user message
      set((state) => {
        state.messages.push({ role: 'user', content });
        state.messages.push({ role: 'assistant', content: '' });
        state.isStreaming = true;
        state.error = null;
      });

      // Build history from previous messages (exclude the empty assistant placeholder)
      const allMessages = get().messages;
      const history = allMessages.slice(0, -2).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      abortController = new AbortController();

      // Include active sheet context so the AI knows what the user is looking at.
      const { useFinancialModelV2Store } = await import('./financial-model-v2');
      const activeSheet = useFinancialModelV2Store.getState().activeSheet;

      try {
        const resp = await fetch('/api/v1/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
          body: JSON.stringify({ modelId, message: content, history, activeSheet }),
          signal: abortController.signal,
        });

        if (resp.status === 401) {
          useAuthStore.getState().logout();
          return;
        }

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.detail || `Chat request failed (${resp.status})`);
        }

        const reader = resp.body?.getReader();
        if (!reader) throw new Error('No response stream');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          // Keep the last partial line in the buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') break;

            try {
              const parsed = JSON.parse(data);
              if (parsed.error) {
                set((state) => {
                  state.error = parsed.error;
                  state.isStreaming = false;
                });
                return;
              }
              if (parsed.text) {
                set((state) => {
                  const last = state.messages[state.messages.length - 1];
                  if (last && last.role === 'assistant') {
                    last.content += parsed.text;
                  }
                });
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        set((state) => {
          state.error = err instanceof Error ? err.message : 'Chat failed';
          // Remove empty assistant message on error
          const last = state.messages[state.messages.length - 1];
          if (last && last.role === 'assistant' && last.content === '') {
            state.messages.pop();
          }
        });
      } finally {
        abortController = null;
        set((state) => {
          state.isStreaming = false;
        });
      }
    },

    clearChat: (modelId?: string) => {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      set((state) => {
        state.messages = [];
        state.isStreaming = false;
        state.error = null;
      });

      // Fire backend delete if modelId provided
      if (modelId) {
        fetch(`/api/v1/chat/history/${modelId}`, {
          method: 'DELETE',
          headers: authHeaders(),
        }).catch(() => {});
      }
    },

    togglePanel: () => {
      set((state) => {
        state.isOpen = !state.isOpen;
      });
    },

    setOpen: (open: boolean) => {
      set((state) => {
        state.isOpen = open;
      });
    },
  })),
);
